import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import usePaymentReturn from './usePaymentReturn';
import PaymentResultOverlay from './PaymentResultOverlay';
import { hasBookingDraft, clearBookingDraft } from './ClientRequests/bookingRequestDraft';
import { loadBookingEditDraft, clearBookingEditDraft } from './ClientRequests/bookingEditDraft';
import { getTopupBookingIntent, clearTopupBookingIntent } from './ClientRequests/topupBookingIntent';
import './paymentReturnPage.css';

// Dedicated hosted-paypage return page — the QUERY-FREE return routes
// /client/payment-return/wallet|invoices|bookings|account (the gateway appends
// ?ref=<order_ref>). Verifies the order against the gateway via
// usePaymentReturn (never trusting the redirect itself) and renders the
// persistent PaymentResultOverlay on a calm branded backdrop; dismissing any
// terminal state navigates to the surface the payment belongs to.
//
// Deliberately NOT wrapped in ProtectedRoute: mobile in-app browsers and
// cross-origin dev setups can land here without a session cookie — the
// verify call then answers 401 and the overlay shows the calm "log in to see
// it" card with a ?next= that round-trips straight back here.

const SURFACE_PATH = {
    wallet: '/client/wallet',
    invoices: '/client/invoices',
    bookings: '/client/bookings',
    account: '/client/profile',
};

export default function PaymentReturnPage() {
    const { surface: surfaceParam } = useParams();
    const navigate = useNavigate();

    const surface = SURFACE_PATH[surfaceParam] ? surfaceParam : 'wallet';
    const targetPath = SURFACE_PATH[surfaceParam] || '/client/home';

    // Captured once — usePaymentReturn keys off it and the URL never changes
    // while this page is mounted (refresh simply re-verifies, idempotently).
    const [orderRef] = useState(() => new URLSearchParams(window.location.search).get('ref'));

    // A wallet TOP-UP paid while a booking draft is waiting is the mid-booking
    // top-up (the client opened "earn bonuses with the wallet" from inside the
    // request modal and paid by card). It is the one paid outcome here that
    // leaves work unfinished — the money is in, the booking is not requested.
    const [resumeDraftOffer, setResumeDraftOffer] = useState(false);

    // After a booking-request payment succeeds, the client should land on the
    // request they just filed — not the generic all-bookings list (owner
    // 2026-07-20). The verify response carries the request_id; we stash the
    // specific request-detail path and dismiss to it instead of `targetPath`.
    const [requestRedirect, setRequestRedirect] = useState(null);

    const payReturn = usePaymentReturn({
        refOverride: orderRef || null,
        onPaid: (data) => {
            const kind = `${data?.kind || ''}`;
            // Clear the draft ONLY for a real booking-request checkout — that
            // is the payment that actually submits the request. The condition
            // used to be "anything that isn't an edit", which swallowed the
            // mid-booking top-up too: the client paid to fund their booking and
            // we deleted the booking they were funding. `topup` keeps its draft.
            // NOTE: the edit-request checkout stakes its gateway_checkouts row
            // with kind='booking_request' too (routes/payments.js ~2302), so
            // this one branch covers both — a paid request, new or changed, is
            // filed and BOTH drafts have done their job.
            // 'autopay_setup' belongs here too: on its success ('verified' —
            // the AUTH+void card-verification rail; or legacy 'paid' from a
            // pre-switch SALE checkout) the staged auto-charge booking request
            // IS filed (routes/payments.js announces it post-commit), so the
            // draft is done — offering "continue my booking request" there
            // would invite a duplicate.
            //
            // DELIBERATELY NOT GATED ON THE SURFACE (item 4, owner 2026-07-19:
            // "after topup done I lose all the fields… the checkout page
            // redirects to wallet"). The topup-checkout endpoint returns to the
            // WALLET surface regardless of the return_path the form asked for
            // (routes/payments.js resolveReturnPath is called with a hardcoded
            // 'wallet'), so the mid-booking top-up lands HERE with
            // surface='wallet' — the old `surfaceParam === 'bookings'` gate is
            // exactly how the owner's half-filled booking got stranded. A
            // waiting draft means unfinished work no matter which surface the
            // paypage came back to.
            if (kind === 'booking_request' || kind === 'autopay_setup') {
                clearBookingDraft();
                clearBookingEditDraft();
                clearTopupBookingIntent();
                // Land on the specific request just filed, when we know it.
                const rid = data?.request_id;
                if (rid) setRequestRedirect(`/client/bookings/requests/${rid}`);
            } else if (
                // ONLY a genuine wallet TOP-UP that was launched FROM the booking
                // form offers to resume it. Two guards, both required:
                //   1. kind === 'topup' — an invoice settlement ('settle_pending'),
                //      a price-adjustment payment, or a failed-charge repayment
                //      ('charge_repay') is NOT a top-up and must never resurface a
                //      booking draft.
                //   2. the intent marker — set by RequestBooking(Edit)Page.openDeposit,
                //      so a plain Wallet-page top-up (also kind 'topup') that just
                //      happens to coincide with a stale 24h draft does not trigger it.
                // The owner's bug (2026-07-19): pay a regular invoice from the
                // Invoices page → "redirected to a random booking request", because
                // the old check offered resume for ANY non-booking payment whenever
                // a leftover draft sat in localStorage.
                kind === 'topup'
                && getTopupBookingIntent()
                && (hasBookingDraft() || loadBookingEditDraft())
            ) {
                setResumeDraftOffer(true);
            } else {
                // Any other paid outcome: the top-up→booking intent (if any) is
                // stale and must not leak into a future unrelated payment.
                clearTopupBookingIntent();
            }
        },
    });

    // No ref in the URL → nothing to verify; go to the surface (or home).
    useEffect(() => {
        if (!orderRef) navigate(targetPath, { replace: true });
    }, [orderRef, navigate, targetPath]);

    // Every dismissal leaves this page for the surface it belongs to — the
    // page has no content of its own to fall back to.
    const wrappedReturn = useMemo(() => ({
        ...payReturn,
        dismiss: () => {
            payReturn.dismiss();
            // Leaving for the surface — any booking top-up intent is spent.
            clearTopupBookingIntent();
            // A just-filed booking request wins over the generic surface path.
            navigate(requestRedirect || targetPath, { replace: true });
        },
    }), [payReturn, navigate, targetPath, requestRedirect]);

    // A saved draft → the cancelled state offers "Edit & resubmit", reopening
    // the request/edit PAGE prefilled from the draft (B1: the forms are pages
    // now, each with its own ?resumeDraft=1 contract — the edit draft's own
    // booking_id names the page). Offered on EVERY surface, not just bookings
    // (item 4): a cancelled mid-booking top-up returns to the WALLET surface
    // (see the onPaid comment above), and the draft is just as stranded there.
    // Two separate drafts, two separate destinations — resuming a change must
    // never reopen the "new booking" form and vice versa. The CREATE draft
    // wins when both exist: it is the only one whose work is unrecoverable
    // (an edit draft's booking still exists and can be reopened by hand).
    const resumeDraft = () => {
        // The intent has done its job the moment the client acts on it.
        clearTopupBookingIntent();
        if (hasBookingDraft()) {
            navigate('/client/bookings/request?resumeDraft=1', { replace: true });
            return;
        }
        const editDraft = loadBookingEditDraft();
        if (editDraft?.booking_id != null) {
            navigate(`/client/bookings/${editDraft.booking_id}/edit-request?resumeDraft=1`, { replace: true });
            return;
        }
        // Draft expired between render and click — the list is the safe landing.
        navigate('/client/bookings', { replace: true });
    };

    const hasAnyDraft = hasBookingDraft() || loadBookingEditDraft() != null;

    const editResubmit = hasAnyDraft ? resumeDraft : null;

    // Same destination, different moment: after a SUCCESSFUL mid-booking top-up.
    const resumeAfterTopUp = resumeDraftOffer ? resumeDraft : null;

    if (!orderRef) return null;

    return (
        <div className="payretPage">
            <div className="payretPage__brand">
                <img src="/logo.png" alt="Holiday Home Cleaning" className="payretPage__logo" />
                <div className="payretPage__hint">Secure payment · verified with the bank</div>
            </div>
            <PaymentResultOverlay
                payReturn={wrappedReturn}
                surface={surface}
                onEditResubmit={editResubmit}
                onResumeDraft={resumeAfterTopUp}
            />
        </div>
    );
}
